import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KPI, milestoneRows, numeric, phaseComparisonBars } from '../intelligence';
import {
  EXECUTION_TONE,
  openActionsInArea,
  conspicuousDeliverables,
  deliverableRows,
  executionSplit,
  phaseExecutionRows,
} from '../detail';
import { criticalityColors } from '../reportTheme';
import type { ReportViewProps } from './viewProps';
import { count, formatDay, pct, scoreStatus, tileDelta } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import ComparisonBarChart from '../widgets/ComparisonBarChart';
import DataTable from '../widgets/DataTable';
import StatusBadge from '../widgets/StatusBadge';
import InlineBar from '../widgets/InlineBar';
import ActionsHint from '../widgets/ActionsHint';
import MilestoneOverview from '../widgets/MilestoneOverview';

/** Signed percentage points, the variance column. */
const pp = (value: number | undefined) =>
  typeof value === 'number' ? `${value > 0 ? '+' : ''}${Math.round(value)} PP` : '—';

/**
 * Execution (Detailmasken §2): are we where the plan says we should be, where
 * do we deviate, and which phases and deliverables cause it. Few management
 * numbers up top, the diagnosis below; QA, dependencies and trends each have
 * their own page and are deliberately not repeated here.
 */
/** "−14", "+3", "±0" - percentage points, rounded. */
const signedPoints = (value: number) => {
  const rounded = Math.round(value);
  return rounded === 0 ? '±0' : rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
};

const Execution: React.FC<ReportViewProps> = ({
  kpis,
  actionPoints,
  historyIndex,
  milestones,
  findings,
  index,
  phaseNames,
  openTab,
  openSection,
  openObject,
}) => {
  const { t } = useTranslation(['templates', 'gantt']);

  const phases = useMemo(() => phaseExecutionRows(kpis, phaseNames), [kpis, phaseNames]);
  const split = useMemo(() => executionSplit(phases), [phases]);
  const deliverables = useMemo(
    () => conspicuousDeliverables(deliverableRows(findings?.deliverables, kpis, phaseNames)),
    [findings, kpis, phaseNames],
  );

  const milestonePlan = useMemo(() => milestoneRows(kpis, milestones), [kpis, milestones]);

  const executionScore = numeric(index, KPI.executionScore);
  const weightedProgress = numeric(index, KPI.projectProgressWeighted);
  const progress = numeric(index, KPI.projectProgress);
  const overdue = numeric(index, KPI.overdueDeliverableCount);
  const deliverableCount = numeric(index, KPI.deliverableCount);

  const share = (n: number) => (split.total > 0 ? (n / split.total) * 100 : undefined);
  const ofPhases = (n: number) => t('report.detail.execution.ofPhases', { count: n, total: split.total });

  // Soll vs. Ist is always the per-phase comparison, never a curve over time.
  const byPhase = useMemo(
    () => phaseComparisonBars(kpis, KPI.expectedPhaseProgress, KPI.phaseProgressWeighted, phaseNames),
    [kpis, phaseNames],
  );

  const executionActions = openActionsInArea(actionPoints, 'execution').length;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        <MetricTile
          label={t('report.kpi.executionScore.label')}
          value={pct(executionScore)}
          delta={tileDelta(t, historyIndex, KPI.executionScore, executionScore)}
          status={scoreStatus(t, executionScore)}
        />
        <MetricTile
          label={t('report.detail.execution.averageProgress')}
          value={pct(progress)}
          delta={tileDelta(t, historyIndex, KPI.projectProgress, progress)}
          bar={{ value: progress }}
          sub={t('report.detail.execution.planned', { value: pct(numeric(index, KPI.expectedProjectProgress)) })}
        />
        <MetricTile
          label={t('report.detail.execution.onPlan')}
          value={pct(share(split.onPlan))}
          sub={ofPhases(split.onPlan)}
          bar={{ value: share(split.onPlan), tone: 'good' }}
        />
        <MetricTile
          label={t('report.detail.execution.behind')}
          value={pct(share(split.behind))}
          sub={ofPhases(split.behind)}
          bar={{ value: share(split.behind), tone: 'warning' }}
        />
        <MetricTile
          label={t('report.detail.execution.critical')}
          value={pct(share(split.critical))}
          sub={ofPhases(split.critical)}
          bar={{ value: share(split.critical), tone: 'critical' }}
        />
        <MetricTile
          label={t('report.cockpit.overdueDeliverables')}
          value={count(overdue)}
          sub={deliverableCount != null ? t('report.detail.ofTotal', { total: Math.round(deliverableCount) }) : undefined}
          status={(overdue ?? 0) > 0 ? { tone: 'critical', label: t('report.detail.attention') } : undefined}
          footer={
            <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('deliverables')}>
              {t('report.detail.toList')}
              <IconArrowRight size={14} stroke={2} />
            </button>
          }
        />
      </div>

      <div className={detailStyles.halves}>
        <div>
          <ChartCard
            title={t('report.detail.execution.planVsActual')}
            subtitle={t('report.detail.execution.planVsActualByPhase')}
          >
            {byPhase.length > 0 ? (
              <ComparisonBarChart
                data={byPhase}
                unit="%"
                max={100}
                plannedLabel={t('report.detail.execution.expected')}
                actualLabel={t('report.detail.execution.actual')}
              />
            ) : (
              <p className={styles.planDescription}>{t('report.state.notComputable')}</p>
            )}
          </ChartCard>
        </div>

        <ChartCard title={t('report.detail.execution.byPhase')} subtitle={t('report.detail.execution.byPhaseHint')} fitContent>
          <DataTable
            rows={phases}
            rowKey={(row) => row.id}
            empty={t('report.state.notComputable')}
            columns={[
              { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.name },
              {
                key: 'actual',
                header: t('report.detail.execution.actual'),
                render: (row) => <InlineBar value={row.actual} />,
              },
              { key: 'expected', header: t('report.detail.execution.expected'), align: 'right', render: (row) => pct(row.expected) },
              {
                key: 'variance',
                header: t('report.detail.col.variance'),
                align: 'right',
                render: (row) => (
                  <span
                    className={
                      row.status === 'CRITICAL'
                        ? detailStyles.critical
                        : row.status === 'BEHIND'
                          ? detailStyles.warning
                          : undefined
                    }
                  >
                    {pp(row.variance)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: t('report.detail.col.status'),
                render: (row) =>
                  row.status ? (
                    <StatusBadge tone={EXECUTION_TONE[row.status]} label={t(`report.detail.executionState.${row.status}`)} />
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <div className={detailStyles.halves}>
      <ChartCard title={t('report.milestones.title')} subtitle={t('report.milestones.subtitle')} fitContent>
        <MilestoneOverview milestones={milestonePlan} />
      </ChartCard>

      <ChartCard
        title={t('report.detail.execution.conspicuous')}
        subtitle={t('report.detail.execution.conspicuousHint')}
        action={
          <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('deliverables')}>
            {t('report.detail.allDeliverables')}
            <IconArrowRight size={14} stroke={2} />
          </button>
        }
        fitContent
      >
        <DataTable
          rows={deliverables}
          rowKey={(row) => row.id as number}
          limit={5}
          empty={t('report.detail.execution.noConspicuous')}
          onRowClick={(row) => openObject?.({ kind: 'deliverable', id: row.id as number })}
          columns={[
            { key: 'name', header: t('report.detail.col.deliverable'), render: (row) => row.name },
            { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.phaseName },
            { key: 'end', header: t('report.detail.col.plannedEnd'), render: (row) => formatDay(row.endDate) },
            {
              key: 'deviation',
              header: t('report.detail.col.deviation'),
              render: (row) =>
                row.daysOverdue != null ? (
                  <span className={detailStyles.critical}>{t('report.detail.daysLate', { count: row.daysOverdue })}</span>
                ) : (
                  <span className={detailStyles.warning}>{pp(row.variance)}</span>
                ),
            },
            { key: 'progress', header: t('report.detail.col.progress'), render: (row) => <InlineBar value={row.progress} /> },
            {
              key: 'status',
              header: t('report.detail.col.status'),
              render: (row) =>
                row.daysOverdue != null ? (
                  <StatusBadge tone="critical" label={t('report.detail.overdue')} />
                ) : row.status ? (
                  <StatusBadge tone={EXECUTION_TONE[row.status]} label={t(`report.detail.executionState.${row.status}`)} />
                ) : (
                  '—'
                ),
            },
            {
              key: 'criticality',
              header: t('report.detail.col.criticality'),
              render: (row) =>
                row.criticality ? (
                  <span style={{ color: criticalityColors[row.criticality], fontWeight: 600 }}>
                    {t(`report.criticality.${row.criticality}`)}
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </ChartCard>
      </div>

      <div className={detailStyles.halves}>
        <ChartCard
          title={t('report.detail.execution.weightingTitle')}
          subtitle={t('report.detail.execution.weightingHint')}
          fitContent
        >
          <div className={detailStyles.compareRows}>
            <div>
              <div className={detailStyles.compareLabel}>
                <span>{t('report.detail.execution.unweighted')}</span>
                <span>{pct(progress)}</span>
              </div>
              <InlineBar value={progress} hideValue />
            </div>
            <div>
              <div className={detailStyles.compareLabel}>
                <span>{t('report.detail.execution.weighted')}</span>
                <span>{pct(weightedProgress)}</span>
              </div>
              <InlineBar value={weightedProgress} tone="warning" hideValue />
            </div>
            {progress != null && weightedProgress != null && (
              <div className={detailStyles.compareFooter}>
                <span>{t('report.detail.execution.weightingGap')}</span>
                <span className={weightedProgress < progress ? detailStyles.critical : detailStyles.good}>
                  {t('report.detail.execution.points', { value: signedPoints(weightedProgress - progress) })}
                </span>
              </div>
            )}
          </div>
        </ChartCard>
      </div>

      <ActionsHint
        text={t('report.detail.actionsHint.execution', { count: executionActions })}
        onOpen={() => openTab?.('actions')}
      />
    </div>
  );
};

export default Execution;
